import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  anonymiseClient,
  archiveClient,
  createClient,
  inviteClient,
  restoreClient,
  revokeClientInvite,
  updateClient,
  type ClientInviteResult,
} from '@/api/clients/clientsApi';
import type { Client, ClientWritePayload } from '@/api/clients/clientsApi.types';
import { CLIENTS_QUERY_KEY, buildClientQueryKey } from '@/constants/queryKeys';

function useInvalidateClients() {
  const queryClient = useQueryClient();
  return function invalidate(clientId?: string) {
    queryClient.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY });
    if (clientId !== undefined) {
      queryClient.invalidateQueries({ queryKey: buildClientQueryKey(clientId) });
    }
  };
}

export function useCreateClientMutation(): UseMutationResult<Client, Error, ClientWritePayload> {
  const invalidate = useInvalidateClients();
  return useMutation({
    mutationFn: createClient,
    onSuccess: function done() {
      invalidate();
    },
  });
}

interface UpdateClientVariables {
  clientId: string;
  payload: ClientWritePayload;
}

export function useUpdateClientMutation(): UseMutationResult<Client, Error, UpdateClientVariables> {
  const invalidate = useInvalidateClients();
  return useMutation({
    mutationFn: function update(variables: UpdateClientVariables) {
      return updateClient(variables.clientId, variables.payload);
    },
    onSuccess: function done(client) {
      invalidate(client.id);
    },
  });
}

export function useArchiveClientMutation(): UseMutationResult<Client, Error, string> {
  const invalidate = useInvalidateClients();
  return useMutation({
    mutationFn: archiveClient,
    onSuccess: function done(client) {
      invalidate(client.id);
    },
  });
}

export interface AnonymiseClientInput {
  clientId: string;
  confirmation: string;
}

export function useAnonymiseClientMutation(): UseMutationResult<
  Client,
  Error,
  AnonymiseClientInput
> {
  const invalidate = useInvalidateClients();
  return useMutation({
    mutationFn: function submitAnonymisation(input: AnonymiseClientInput) {
      return anonymiseClient(input.clientId, input.confirmation);
    },
    onSuccess: function done(client) {
      invalidate(client.id);
    },
  });
}

export function useRestoreClientMutation(): UseMutationResult<Client, Error, string> {
  const invalidate = useInvalidateClients();
  return useMutation({
    mutationFn: restoreClient,
    onSuccess: function done(client) {
      invalidate(client.id);
    },
  });
}

export function useInviteClientMutation(): UseMutationResult<ClientInviteResult, Error, string> {
  const invalidate = useInvalidateClients();
  return useMutation({
    mutationFn: inviteClient,
    onSuccess: function done(result) {
      invalidate(result.client.id);
    },
  });
}

export function useRevokeClientInviteMutation(): UseMutationResult<Client, Error, string> {
  const invalidate = useInvalidateClients();
  return useMutation({
    mutationFn: revokeClientInvite,
    onSuccess: function done(client) {
      invalidate(client.id);
    },
  });
}
